import React from 'react';
import { captureException, withScope } from '@sentry/react';
import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import type { ErrorMessage } from '../../generated-api';
import { ApiError } from '../api';
import { NetworkError } from '../utils/error-handler';
import { isProblemDetail } from '../utils/problemDetail';

const dateFormatter = Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  month: '2-digit',
  second: '2-digit',
  year: 'numeric',
});

interface ErrorBoundaryProps {
  children: React.ReactNode;
  title?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorTitle?: string;
  errorDescription?: string;
  timestamp?: string;
  traceId?: string;
  errorMessage?: string;
}

// Description
// Timestamp
// TraceId

const isChunkLoadError = (error: Error): boolean => {
  const message = error.message || '';
  return (
    message.includes('Importing a module script failed') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk')
  );
};

const RELOAD_KEY = 'epos.chunk-reload';

const defaultState: ErrorBoundaryState = {
  errorDescription: '',
  errorMessage: 'Something went wrong, please take a screenshot and send it to Support',
  errorTitle: 'Unknown Error',
  hasError: false,
  timestamp: undefined,
  traceId: '',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const instanceOfErrorMessage = (object: any): object is ErrorMessage => 'statusCode' in object;
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { ...defaultState, errorTitle: props.title };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_error: any): ErrorBoundaryState {
    if (instanceOfErrorMessage(_error)) {
      return {
        errorDescription: _error.message ? _error.message : _error.description,
        hasError: true,
        timestamp: _error.timestamp
          ? dateFormatter.format(new Date(Number(_error.timestamp) * 1000))
          : dateFormatter.format(new Date(Date.now())),
        traceId: _error.traceId ? _error.traceId : defaultState.traceId,
      };
    }

    if (isProblemDetail(_error)) {
      return {
        errorDescription: _error.detail || _error.message || '',
        errorTitle: _error.title ?? `Error (${_error.status})`,
        hasError: true,
        timestamp: dateFormatter.format(new Date()),
        traceId: _error.properties?.trace || '',
      };
    }

    if (_error instanceof ApiError) {
      return {
        errorDescription: _error.message,
        errorTitle: _error.title ?? `API _error (${_error.response.status})`,
        hasError: true,
        timestamp: dateFormatter.format(new Date()),
        traceId: _error.traceId || '',
      };
    }

    if (_error instanceof NetworkError) {
      return {
        errorDescription: _error.message,
        errorTitle: 'Network Error',
        hasError: true,
        timestamp: dateFormatter.format(new Date()),
      };
    }

    return {
      errorDescription: _error.message ? _error.message?.toString() : defaultState.errorMessage,
      errorTitle: defaultState.errorTitle,
      hasError: true,
      timestamp: dateFormatter.format(new Date(Date.now())),
    };
  }

  componentDidCatch(error: Error | ErrorMessage, errorInfo: React.ErrorInfo) {
    if (error instanceof Error && isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
        return;
      }
    }

    if (!instanceOfErrorMessage(error)) {
      withScope((scope) => {
        scope.setTag('errorType', error.name);
        scope.setExtra('ErrorInfo', JSON.stringify(errorInfo));

        if (error instanceof ApiError) {
          scope.setExtra('errorBody', error.errorBody);
          scope.setExtra('responseStatus', error.response.status);
          scope.setContext('ResponseHeaders', Object.fromEntries(error.response.headers.entries()));
        }

        if ('originalError' in error) {
          scope.setContext(
            'OriginalError',
            error.originalError as Record<string, unknown> | null
          );
        }

        captureException(error);
      });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Dialog
          className="w-fit max-w-[50%]"
          title={this.state.errorTitle ? this.state.errorTitle : 'Something went wrong'}
          onClose={() => this.setState(defaultState)}
        >
          <div className="flex flex-col">
            <div>
              <p>{this.state.errorDescription}</p>
              <div className="text-grey-dark mt-6">
                {this.state.timestamp && <p className=" self-start text-sm mb-2">Timestamp : {this.state.timestamp}</p>}
                {this.state.traceId && <p className=" self-start text-sm">TraceId : {this.state.traceId}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button className="button secondary" kind="secondary" onClick={() => this.setState(defaultState)}>
                Close
              </Button>
            </div>
          </div>
        </Dialog>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
